
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Download, Smartphone, Apple, ChevronRight, Shield, Zap, Clock } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

export function DownloadSection() {
  const { t } = useLanguage();
  const downloadRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animated-element');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('appear');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (downloadRef.current) {
      observer.observe(downloadRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-b from-white via-blue-50 to-indigo-50"
      ref={downloadRef}
      id="download"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-transparent"></div>
      <div className="absolute -top-24 right-10 md:right-40 w-64 h-64 rounded-full bg-gradient-to-tr from-purple-200 to-blue-200 opacity-40 filter blur-3xl"></div>
      <div className="absolute bottom-10 left-10 md:left-40 w-72 h-72 rounded-full bg-gradient-to-br from-blue-200 to-indigo-300 opacity-30 filter blur-3xl"></div>
      
      <div className="container px-4 md:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-16">
          {/* Content */}
          <div className="lg:w-1/2 text-center lg:text-left">
            <div className="animated-element">
              {/*<span className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-6 shadow-sm">
                {t('common.downloadApp')}
              </span>*/}
            </div>
            
            <h2 className="animated-element text-3xl md:text-4xl xl:text-5xl font-bold mb-6 text-gray-900">
              {t('home.download.title')}
            </h2>
            
            <p className="animated-element text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              {t('home.download.subtitle')}
            </p>
            
            <div className="animated-element space-y-4 mb-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900">{t('home.download.simple')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.download.simpleDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900">{t('home.download.realTime')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.download.realTimeDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900">{t('home.download.securePayment')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.download.securePaymentDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900">{t('home.download.alwaysOnline')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.download.alwaysOnlineDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="animated-element flex flex-col sm:flex-row justify-center lg:justify-start gap-4 mb-10">
              <Button 
                size="lg" 
                className="group relative overflow-hidden rounded-xl px-8 py-6 bg-blue-600 hover:bg-blue-700 transition-all duration-300 shadow-md"
              >
                <a href="https://apps.apple.com/us/app/sportlogin/id6443801938" target="blank" className="relative z-10 flex items-center gap-2">
                  <Apple className="w-6 h-6" />
                  <div className="flex flex-col items-start">
                    <span className="text-xs opacity-90">Télécharger sur</span>
                    <span className="font-semibold">{t('home.download.ios')}</span>
                  </div>
                </a>
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1 duration-300">
                  <ChevronRight className="w-5 h-5 text-white" />
                </span>
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="group relative overflow-hidden rounded-xl px-8 py-6 border-2 border-blue-500 hover:bg-blue-50 transition-all duration-300 shadow-sm"
              >
                <a href="https://play.google.com/store/apps/details?id=com.sportlogin.app" target="blank" className="relative z-10 flex items-center gap-2">
                  <Download className="w-6 h-6 text-blue-600" />
                  <div className="flex flex-col items-start">
                    <span className="text-xs opacity-90">Télécharger sur</span>
                    <span className="font-semibold text-blue-600">{t('home.download.android')}</span>
                  </div>
                </a>
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1 duration-300">
                  <ChevronRight className="w-5 h-5 text-blue-600" />
                </span>
              </Button>
            </div>
          </div>
          
          {/* Device mockups */}
          <div className="lg:w-1/2 relative">
            <div className="lg:w-1/2 flex justify-center">
            <div className="animated-element">
              {/* Phone mockup */}
              <div className={`relative z-20 mx-auto ${isMobile ? 'max-w-[200px]' : 'max-w-[280px]'}`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-[40px] blur-xl opacity-20 transform -rotate-6"></div>
                  <div className={`relative border-black rounded-[40px] overflow-hidden shadow-2xl transform rotate-6 transition-all duration-500 hover:rotate-0 ${isMobile ? 'border-[5px]' : 'border-[10px]'}`}>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-6 bg-black z-10 rounded-b-xl"></div>
                    <img
                      src="https://storage.googleapis.com/sportlogin/frontend/sdfsdfsdf0sd80f8sdfsdf.png"
                      alt="SPORTLOGIN mobile app"
                      className="w-full h-auto object-cover rounded-[30px]"
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Second phone (slightly behind and to the right) */}
                <div className={`absolute -bottom-10 left-1/2 transform -translate-x-1/3 z-10 ${isMobile ? 'w-[180px]' : 'w-[220px]'}`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-500 rounded-[40px] blur-xl opacity-20 transform rotate-12"></div>
                  <div className={`relative border-black rounded-[40px] overflow-hidden shadow-xl transform -rotate-12 transition-all duration-500 hover:rotate-0 ${isMobile ? 'border-[5px]' : 'border-[10px]'}`}>
                    <img
                      src="https://storage.googleapis.com/sportlogin/frontend/9898tgfgdfs51ds1fsdf.png"
                      alt="SPORTLOGIN agenda screen"
                      className="w-full h-auto object-cover rounded-[30px]"
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Floating features badge */}
                <div className="absolute -top-4 -right-4 bg-white rounded-full p-1 shadow-lg z-30 animated-element" style={{ animationDelay: '0.5s' }}>
                  <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Background glow effect */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] h-[600px] bg-gradient-to-r from-blue-400 to-indigo-500 opacity-10 rounded-full filter blur-3xl -z-10"></div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}
